import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notesDb } from '../../lib/db/index.js';
import { useAuth } from '../../lib/firebase/auth-context.js';
import { noteInputSchema } from '../../lib/schemas/note.js';
import type { Visibility } from '../../types/visibility.js';
import { toAppError } from '../../lib/utils/error.js';
import { Spinner } from '../shared/Spinner.js';
import { MarkdownEditor } from '../shared/MarkdownEditor.js';
import { TagInput } from './TagInput.js';
import { VisibilityRadio } from './VisibilityRadio.js';
import { useDraft } from './useDraft.js';
import { DraftRestoredBanner } from './DraftRestoredBanner.js';
import { ProjectSelectField } from './ProjectSelectField.js';

export interface NoteDraftState {
  title: string;
  purpose: string;
  tried: string;
  result: string;
  conclusion: string;
  tags: string[];
  visibility: Visibility;
  projectId?: string | undefined;
}

const INITIAL: NoteDraftState = {
  title: '',
  purpose: '',
  tried: '',
  result: '',
  conclusion: '',
  tags: [],
  visibility: 'private',
};

interface NoteFormProps {
  onDone: (id: string) => void;
  editId?: string | undefined;
  initialValues?: NoteDraftState | undefined;
}

export function NoteForm({ onDone, editId, initialValues }: NoteFormProps) {
  const isEditing = !!editId;
  const { fbUser } = useAuth();
  const qc = useQueryClient();
  const draftHook = useDraft<NoteDraftState>('note', INITIAL);
  const [editDraft, setEditDraft] = useState<NoteDraftState>(initialValues ?? INITIAL);
  const draft = isEditing ? editDraft : draftHook.value;
  const setDraft = isEditing ? setEditDraft : draftHook.set;
  const clearDraft = isEditing ? () => {} : draftHook.clear;
  const wasRestored = isEditing ? false : draftHook.wasRestored;
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof NoteDraftState>(k: K, v: NoteDraftState[K]) =>
    setDraft({ ...draft, [k]: v });

  const m = useMutation({
    mutationFn: async () => {
      if (!fbUser) throw new Error('not signed in');
      const parsed = noteInputSchema.parse({
        title: draft.title,
        purpose: draft.purpose,
        tried: draft.tried,
        result: draft.result,
        conclusion: draft.conclusion,
        tags: draft.tags,
        links: [],
        visibility: draft.visibility,
        projectId: draft.projectId,
      });
      const payload = {
        title: parsed.title,
        purpose: parsed.purpose,
        tried: parsed.tried,
        result: parsed.result,
        conclusion: parsed.conclusion,
        tags: parsed.tags,
        links: parsed.links,
        visibility: parsed.visibility as Visibility,
        projectId: parsed.projectId,
      };
      if (editId) {
        // attachments は編集モードでは触らない（添付 UI 自体が現状ない）
        await notesDb.update(editId, payload);
        return editId;
      }
      return await notesDb.create(fbUser.uid, { ...payload, attachments: [] });
    },
    onSuccess: async (id) => {
      clearDraft();
      await qc.invalidateQueries({ queryKey: ['notes'] });
      await qc.invalidateQueries({ queryKey: ['home', 'recentNotes'] });
      await qc.invalidateQueries({ queryKey: ['projects', 'counts'] });
      await qc.invalidateQueries({ queryKey: ['project'] });
      if (editId) {
        await qc.refetchQueries({ queryKey: ['note', editId] });
      }
      onDone(id);
    },
    onError: (err) => setError(toAppError(err).userMessage),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    m.mutate();
  };

  return (
    <form className="compose-form" onSubmit={onSubmit}>
      {!isEditing && (
        <DraftRestoredBanner visible={wasRestored} onDiscard={clearDraft} />
      )}
      <label className="auth-field">
        <span>タイトル</span>
        <input required value={draft.title} onChange={(e) => set('title', e.target.value)} />
      </label>
      <div className="auth-field">
        <span>目的 <em className="mono">何を確認したかったか</em></span>
        <MarkdownEditor
          value={draft.purpose}
          onChange={(v) => set('purpose', v)}
          rows={2}
          maxLength={4000}
          ariaLabel="目的"
        />
      </div>
      <div className="auth-field">
        <span>試したこと <em className="mono">設定・手順・条件</em></span>
        <MarkdownEditor
          value={draft.tried}
          onChange={(v) => set('tried', v)}
          rows={4}
          maxLength={8000}
          ariaLabel="試したこと"
        />
      </div>
      <div className="auth-field">
        <span>結果 <em className="mono">うまくいった点・課題</em></span>
        <MarkdownEditor
          value={draft.result}
          onChange={(v) => set('result', v)}
          rows={3}
          maxLength={4000}
          ariaLabel="結果"
        />
      </div>
      <div className="auth-field">
        <span>結論 <em className="mono">採用可否・今後の対応</em></span>
        <MarkdownEditor
          value={draft.conclusion}
          onChange={(v) => set('conclusion', v)}
          rows={2}
          maxLength={2000}
          ariaLabel="結論"
        />
      </div>
      <div className="auth-field">
        <span>タグ</span>
        <TagInput value={draft.tags} onChange={(t) => set('tags', t)} />
      </div>
      <ProjectSelectField
        value={draft.projectId}
        onChange={(v) => set('projectId', v)}
      />
      <VisibilityRadio value={draft.visibility} onChange={(v) => set('visibility', v)} />
      {error && <div className="auth-error">{error}</div>}
      <div className="compose-foot">
        <span className="mono compose-draft-hint">
          {isEditing ? '編集モード' : '下書きは自動保存されます'}
        </span>
        <button type="submit" className="btn btn-primary" disabled={m.isPending}>
          {m.isPending ? <Spinner /> : isEditing ? '更新' : 'メモを保存'}
        </button>
      </div>
    </form>
  );
}
